/**
 * Visitor attribution utilities — Ticket 8.
 *
 * Responsibilities:
 *   - Create/persist `anonymous_id` as a first-party cookie (opaque UUID, NOT derived from PII).
 *   - Create/persist `session_id` in sessionStorage (expires on tab close).
 *   - Read UTM params, click IDs, and page context from the current URL.
 *   - Fire-and-forget POST to the backend attribution endpoint (Ticket 7).
 *   - Export `getAttributionIds()` so signup/register payloads can include the IDs.
 *
 * CTO review note applied:
 *   - anonymous_id is a random UUID — never derived from email or any identifiable data.
 *   - Cookie domain is read from REACT_APP_ATTRIBUTION_COOKIE_DOMAIN env var.
 *     Set to `.meetocto.com` in production so www and app subdomains share the same cookie.
 *     go.meetocto-mail.com has a different registrable domain and cannot share it.
 *   - SameSite=Lax; Secure (when on HTTPS).
 *   - Consent: captureAttribution() is a no-op when the consent guard returns false.
 *     Wire the consent check to your cookie-consent solution before launch.
 *   - No emails or PII are read from URL params or stored anywhere in this module.
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ANON_ID_COOKIE  = 'mo_anon_id';
const SESSION_ID_KEY  = 'mo_session_id';

/** Fields to read from window.location.search (mapped 1:1 to the backend field names). */
const URL_FIELD_MAP = {
  utm_source:        'utm_source',
  utm_medium:        'utm_medium',
  utm_campaign:      'utm_campaign',
  utm_term:          'utm_term',
  utm_content:       'utm_content',
  gclid:             'gclid',
  fbclid:            'fbclid',
  msclkid:           'msclkid',
  ttclid:            'ttclid',
  email_campaign_id: 'email_campaign_id',
  email_list_id:     'email_list_id',
  email_creative_id: 'email_creative_id',
  lead_id:           'lead_id',
  affiliate_id:      'affiliate_id',
  // existing peer-referral param → maps to referral_code on the backend
  referral:          'referral_code',
  referral_code:     'referral_code',
};

// ---------------------------------------------------------------------------
// UUID generator (no external dependency required)
// ---------------------------------------------------------------------------

function generateUUID() {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  // Fallback: RFC 4122 v4 UUID without crypto.randomUUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    // eslint-disable-next-line no-bitwise
    const r = (Math.random() * 16) | 0;
    // eslint-disable-next-line no-bitwise
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ---------------------------------------------------------------------------
// Cookie helpers
// ---------------------------------------------------------------------------

function getCookieDomain() {
  // e.g. REACT_APP_ATTRIBUTION_COOKIE_DOMAIN=.meetocto.com
  return process.env.REACT_APP_ATTRIBUTION_COOKIE_DOMAIN || '';
}

function setCookie(name, value, days) {
  try {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    const domain  = getCookieDomain();
    const domainAttr = domain ? `; domain=${domain}` : '';
    const secureAttr =
      typeof window !== 'undefined' && window.location.protocol === 'https:'
        ? '; Secure'
        : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/${domainAttr}; SameSite=Lax${secureAttr}`;
  } catch (_) {
    // Cookie write may be blocked in some environments — fail silently.
  }
}

function getCookie(name) {
  try {
    const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');
    const match = document.cookie.match(
      new RegExp(`(?:^|; )${escaped}=([^;]*)`)
    );
    return match ? decodeURIComponent(match[1]) : null;
  } catch (_) {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Public: anonymous_id (first-party cookie, 365 days)
// ---------------------------------------------------------------------------

export function getOrCreateAnonymousId() {
  try {
    let id = getCookie(ANON_ID_COOKIE);
    if (!id) {
      id = generateUUID();
      setCookie(ANON_ID_COOKIE, id, 365);
    }
    return id;
  } catch (_) {
    return generateUUID(); // ephemeral fallback — won't persist
  }
}

// ---------------------------------------------------------------------------
// Public: session_id (sessionStorage — expires on tab close)
// ---------------------------------------------------------------------------

export function getOrCreateSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_ID_KEY);
    if (!id) {
      id = generateUUID();
      sessionStorage.setItem(SESSION_ID_KEY, id);
    }
    return id;
  } catch (_) {
    // sessionStorage may be blocked in private browsing or iframe sandboxes.
    return generateUUID(); // ephemeral fallback
  }
}

// ---------------------------------------------------------------------------
// Public: getAttributionIds — used in register / booking payloads
// ---------------------------------------------------------------------------

/**
 * Returns { anonymous_id, session_id } for inclusion in API payloads.
 * Never throws.
 */
export function getAttributionIds() {
  try {
    return {
      anonymous_id: getOrCreateAnonymousId(),
      session_id:   getOrCreateSessionId(),
    };
  } catch (_) {
    return {};
  }
}

// ---------------------------------------------------------------------------
// Consent guard (stub — wire to your cookie-consent solution before launch)
// ---------------------------------------------------------------------------

/**
 * Returns true when attribution tracking is allowed.
 *
 * In v1 this always returns true. Before public launch, wire this to
 * your cookie-consent banner (e.g. check a consent cookie or a global
 * window.__moConsentGranted flag set by the banner).
 */
function consentGranted() {
  // TODO: replace with real consent check (e.g. CookieYes, Cookiebot, custom banner)
  return true;
}

// ---------------------------------------------------------------------------
// URL param reader
// ---------------------------------------------------------------------------

function readUrlParams() {
  try {
    const params = new URLSearchParams(window.location.search);
    const result = {};
    Object.entries(URL_FIELD_MAP).forEach(([urlKey, fieldKey]) => {
      const val = params.get(urlKey);
      if (val) {
        // Truncate at backend column limit so we never submit oversize values
        result[fieldKey] = val.substring(0, fieldKey === 'referral_code' ? 255 : 255);
      }
    });
    return result;
  } catch (_) {
    return {};
  }
}

// ---------------------------------------------------------------------------
// Public: captureAttribution — call once per hard page load
// ---------------------------------------------------------------------------

/**
 * Reads the current URL params, creates/reads anonymous_id + session_id,
 * and fires a POST to the backend attribution endpoint.
 *
 * Fire-and-forget — never throws, never blocks rendering.
 * Safe to call in the App module body or a root useEffect.
 */
export function captureAttribution() {
  try {
    if (!consentGranted()) return;

    const anonymousId = getOrCreateAnonymousId();
    const sessionId   = getOrCreateSessionId();
    const urlParams   = readUrlParams();

    const payload = {
      anonymous_id: anonymousId,
      session_id:   sessionId,
      landing_page: (window.location.href  || '').substring(0, 2048),
      referrer:     (document.referrer     || '').substring(0, 2048),
      ...urlParams,
    };

    // Same resolution as axios-instance.js / BACKEND_API_BASE_URL:
    // prefer REACT_APP_API_BASE_URL; else same-origin Laravel public API path.
    // (Previously empty env skipped the POST entirely while still setting mo_anon_id.)
    const apiBase = (
      process.env.REACT_APP_API_BASE_URL ||
      (typeof window !== 'undefined'
        ? `${window.location.origin}/backend/public/api`
        : '')
    ).replace(/\/$/, '');
    if (!apiBase) return; // SSR / no window — skip

    const url = `${apiBase}/analytics/attribution`;

    // keepalive: true allows the request to survive page unload / navigation
    fetch(url, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {}); // intentional no-op — backend is fail-open too
  } catch (_) {
    // Attribution capture must never break the host page.
  }
}
